import type { EngagementInput, EngagementRate } from '@clockwise/clients/sows';
import { INVOICE_FLAG_REASON_LABELS } from '@clockwise/shared';
import { useMemo } from 'react';
import { isRateSetCustomized } from '@/components/custom/engagement-rate-table';
import { type ProductDisplayLookup, ProductLabel } from '@/components/custom/product-label';
import {
  BILLING_MODEL_LABELS,
  ENGAGEMENT_CADENCE_LABELS,
  formatDollars,
} from '@/lib/engagement-format';
import { formatRate } from '@/lib/format-rate';
import { cn } from '@/lib/utils';

/**
 * One engagement, read-only. Interactive in amend mode (opens the editor).
 *
 * Every card discloses its full scope inline — activities, rates or tiers —
 * rather than behind a hover, so a SOW can be read at a glance.
 */
export function EngagementCard({
  engagement,
  interactive = true,
  onClick,
  productLookup,
  roleLookup,
  rateCardName,
  customerCadenceLabel,
}: {
  engagement: EngagementInput;
  /** When false, renders as a static block — no hover affordance, no click. */
  interactive?: boolean;
  onClick?: () => void;
  productLookup: ProductDisplayLookup;
  roleLookup: (id: string) => string;
  /** Name of the SOW's selected rate card — shown as a T&M engagement's
   *  source badge. Null when no card is selected. */
  rateCardName: string | null;
  /** What an engagement naming no cadence of its own falls back to. */
  customerCadenceLabel: string;
}) {
  const tmActivities = useMemo(
    () =>
      engagement.billingModel === 'tm'
        ? groupActivitiesByRate(engagement.activities, engagement.rates, productLookup)
        : null,
    [engagement, productLookup],
  );
  // T&M activities sit in their role's rate row; only the ones whose role has
  // no rate here are listed on their own. An empty list is still stated.
  const listedActivities =
    tmActivities && engagement.activities.length > 0
      ? tmActivities.unmatched
      : engagement.activities;

  const body = (
    <div className="min-w-0 flex-1 text-sm">
      <p className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium">{engagement.name}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {BILLING_MODEL_LABELS[engagement.billingModel]}
        </span>
        {engagement.billingModel === 'retainer' ? (
          <>
            <Dot />
            <span>{formatDollars(engagement.fee)}</span>
          </>
        ) : null}
        <Dot />
        <span className="text-muted-foreground">
          {engagement.billingCadence
            ? ENGAGEMENT_CADENCE_LABELS[engagement.billingCadence]
            : `${customerCadenceLabel} (from customer)`}
        </span>
        {engagement.consolidate === false ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            Separate invoice
          </span>
        ) : null}
        {engagement.manual ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {INVOICE_FLAG_REASON_LABELS.MANUAL_REVIEW}
          </span>
        ) : null}
      </p>

      {engagement.description ? (
        <p className="mt-1 truncate text-xs text-muted-foreground">{engagement.description}</p>
      ) : null}

      {engagement.billingModel === 'tm' ? (
        <RateList
          rates={engagement.rates}
          roleLookup={roleLookup}
          rateCardName={rateCardName}
          activitiesByRole={tmActivities?.byRole}
        />
      ) : null}

      {engagement.billingModel === 'metrics' ? <TierList engagement={engagement} /> : null}

      {engagement.billingModel === 'retainer' && (engagement.overageRates ?? []).length > 0 ? (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground">Overage</p>
          <RateList
            rates={engagement.overageRates ?? []}
            roleLookup={roleLookup}
            rateCardName={null}
          />
        </div>
      ) : null}

      {listedActivities.length > 0 || engagement.activities.length === 0 ? (
        <ActivityList
          activities={listedActivities}
          productLookup={productLookup}
          scoped={engagement.billingModel !== 'tm'}
        />
      ) : null}
    </div>
  );

  const baseClasses = 'flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left';
  if (!interactive) return <div className={baseClasses}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(baseClasses, 'transition hover:bg-muted/50')}
    >
      {body}
    </button>
  );
}

/**
 * Files each T&M activity under its primary role's rate row, so a role reads
 * as one line: role, the activities attributed to it, rate. An activity whose
 * role has no rate on the engagement (or whose product didn't resolve) is
 * returned as `unmatched` and listed separately. One still loading (no role
 * id, role code `undefined`) is left out of both until it resolves, so the
 * card doesn't render it as a separate row and then jump it into a rate row.
 */
function groupActivitiesByRate(
  activities: EngagementInput['activities'],
  rates: readonly EngagementRate[],
  productLookup: ProductDisplayLookup,
): { byRole: Map<string, string[]>; unmatched: EngagementInput['activities'] } {
  const rateRoles = new Set(rates.map((r) => r.roleId));
  const byRole = new Map<string, string[]>();
  const unmatched: EngagementInput['activities'] = [];
  for (const a of activities) {
    const info = productLookup(a.productId);
    if (!info.serviceRoleId && info.serviceRoleCode === undefined) continue;
    if (info.serviceRoleId && rateRoles.has(info.serviceRoleId)) {
      const names = byRole.get(info.serviceRoleId) ?? [];
      names.push(info.name);
      byRole.set(info.serviceRoleId, names);
    } else {
      unmatched.push(a);
    }
  }
  return { byRole, unmatched };
}

function ActivityList({
  activities,
  productLookup,
  scoped,
}: {
  activities: EngagementInput['activities'];
  productLookup: ProductDisplayLookup;
  /** False for T&M: its list only attributes time, it is not a scope. */
  scoped: boolean;
}) {
  if (activities.length === 0) {
    // An empty list is MEANINGFUL — it matches every activity — so it is
    // stated rather than rendered as nothing, which would read as missing
    // data on a card whose whole job is to disclose scope.
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        {scoped ? 'Covers every activity' : 'Any activity can be attributed here'}
      </p>
    );
  }
  return (
    <ul className="mt-1 divide-y divide-border/60">
      {activities.map((a) => {
        const info = productLookup(a.productId);
        return (
          <li key={a.productId} className="py-1">
            <ProductLabel
              name={info.name}
              serviceRoleCode={info.serviceRoleCode}
              className="text-muted-foreground"
            />
          </li>
        );
      })}
    </ul>
  );
}

function RateList({
  rates,
  roleLookup,
  rateCardName,
  activitiesByRole,
}: {
  rates: readonly EngagementRate[];
  roleLookup: (id: string) => string;
  rateCardName: string | null;
  /** Activity names per role id, shown between the role and its rate. */
  activitiesByRole?: ReadonlyMap<string, readonly string[]>;
}) {
  const list = rates;
  const customized = isRateSetCustomized(list);
  return (
    <>
      <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {list.length} {list.length === 1 ? 'role' : 'roles'}
        {/* Source rate card (green) — the global card these rates derive from.
         *  Plus an orange "Customized" badge when any rate was overridden off
         *  that card (rate ≠ baseRate). */}
        {rateCardName ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">
            Global · {rateCardName}
          </span>
        ) : null}
        {customized ? (
          <span className="rounded-full bg-orange-100 px-2 py-0.5 font-medium text-orange-900 dark:bg-orange-900/30 dark:text-orange-200">
            Customized
          </span>
        ) : null}
      </p>
      <ul className="mt-1 divide-y divide-border/60">
        {list.map((r) => {
          const overridden = r.rate !== r.baseRate;
          const label = roleLookup(r.roleId);
          return (
            <li
              key={r.roleId}
              data-testid="rate-row"
              className="flex items-baseline justify-between gap-3 py-1"
            >
              {/* With activities, the role is a fixed column and the names wrap
               *  inside their own column beside it — never onto a line under
               *  the role. Without, the label keeps the full row. */}
              {activitiesByRole ? (
                <span className="flex min-w-0 flex-1 items-baseline gap-3">
                  <span className="w-44 shrink-0 truncate text-muted-foreground" title={label}>
                    {label}
                  </span>
                  <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                    {activitiesByRole.get(r.roleId)?.join(', ')}
                  </span>
                </span>
              ) : (
                <span className="truncate text-muted-foreground">{label}</span>
              )}
              <span className="shrink-0 tabular-nums">
                {formatRate(r.rate)}/h
                {overridden ? (
                  <span className="ml-1.5 text-xs text-muted-foreground line-through">
                    {formatRate(r.baseRate)}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function TierList({
  engagement,
}: {
  engagement: Extract<EngagementInput, { billingModel: 'metrics' }>;
}) {
  return (
    <>
      <p className="mt-1 text-xs text-muted-foreground">{engagement.metricName}</p>
      <ul className="mt-1 divide-y divide-border/60">
        {engagement.tiers.map((t, i) => (
          <li
            // Bands are positional; their bounds are the data, not the key.
            key={`tier-${i}`}
            className="flex items-center justify-between gap-3 py-1"
          >
            <span className="truncate text-muted-foreground">
              {t.fromQty}
              {t.toQty == null ? '+' : `–${t.toQty}`}
            </span>
            <span className="shrink-0 tabular-nums">{formatDollars(t.price)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function Dot() {
  return <span className="text-muted-foreground">·</span>;
}
